/** Standard DAMM quasigroup for decimal check digits. */
const DAMM_TABLE: readonly (readonly number[])[] = [
  [0, 3, 1, 7, 5, 9, 8, 6, 4, 2],
  [7, 0, 9, 2, 1, 5, 4, 8, 6, 3],
  [4, 2, 0, 6, 8, 7, 1, 3, 5, 9],
  [1, 7, 5, 0, 9, 8, 3, 4, 2, 6],
  [6, 1, 2, 3, 0, 4, 5, 9, 7, 8],
  [3, 6, 7, 4, 2, 0, 9, 5, 8, 1],
  [5, 8, 6, 9, 7, 2, 0, 1, 3, 4],
  [8, 9, 4, 5, 3, 6, 2, 0, 7, 1],
  [9, 4, 3, 8, 6, 1, 7, 2, 0, 5],
  [2, 5, 8, 1, 4, 3, 6, 9, 7, 0],
];

export const QUICK_SHARE_BODY_DIGITS = 7;
export const QUICK_SHARE_CODE_DIGITS = 8;

/** Strip everything except digits (handles `XXXX - XXXX`, paste with spaces, etc.). */
export function normalizeShareCodeDigits(input: string): string {
  return input.replace(/\D/g, '');
}

/** Format 8 digits as `XXXX - XXXX` for display / dictation. */
export function formatShareCodeDisplay(digits: string): string {
  const normalized = normalizeShareCodeDigits(digits).padStart(QUICK_SHARE_CODE_DIGITS, '0').slice(0, QUICK_SHARE_CODE_DIGITS);
  return `${normalized.slice(0, 4)} - ${normalized.slice(4)}`;
}

function interimDigit(digits: string): number {
  let interim = 0;
  for (const ch of digits) {
    const digit = ch.charCodeAt(0) - 48;
    if (digit < 0 || digit > 9) {
      throw new Error('invalid_digit');
    }
    interim = DAMM_TABLE[interim][digit];
  }
  return interim;
}

/** Compute the DAMM check digit for a decimal body (no check digit yet). */
export function dammCheckDigit(bodyDigits: string): number {
  // Diagonal of the table is zero, so the check digit equals the interim after the body.
  return interimDigit(bodyDigits);
}

export function appendDammCheckDigit(bodyDigits: string): string {
  if (!/^\d+$/.test(bodyDigits)) {
    throw new Error('invalid_body');
  }
  return `${bodyDigits}${dammCheckDigit(bodyDigits)}`;
}

/** True when the full code (body + check digit) is a valid DAMM number. */
export function isValidDammCode(codeDigits: string): boolean {
  const normalized = normalizeShareCodeDigits(codeDigits);
  if (normalized.length !== QUICK_SHARE_CODE_DIGITS || !/^\d+$/.test(normalized)) {
    return false;
  }
  try {
    return interimDigit(normalized) === 0;
  } catch {
    return false;
  }
}

export function generateQuickShareCode(): { digits: string; display: string } {
  const values = crypto.getRandomValues(new Uint32Array(QUICK_SHARE_BODY_DIGITS));
  let body = '';
  for (let i = 0; i < QUICK_SHARE_BODY_DIGITS; i++) {
    body += String(values[i] % 10);
  }
  const digits = appendDammCheckDigit(body);
  return { digits, display: formatShareCodeDisplay(digits) };
}
